import React, { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { connect } from 'react-redux'
import { arrayToObject, EmptyArray, groupBy } from 'lib/array/arrayUtils'
import {
  EXP_CATEGORY_LUX_PLUS_ID,
  EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID,
} from 'constants/experience'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import CheckboxInputListItem from 'LuxKit/List/CheckboxInputListItem'
import BodyText from 'LuxKit/Typography/BodyText'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useMostPopulatedSearchExperienceFacets from '../hooks/useMostPopulatedSearchExperienceFacets'

const LuxPlusMemberPriceLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  color: ${(props) => props.theme.palette.product.luxPlus.altForeground};
`

const ListContainer = styled.div`
  margin-inline: ${rem(-16)};
  margin-bottom: ${rem(-8)};
`

interface MappedProps {
  categories: Array<App.ExperienceItemCategory>;
}

interface Props {
  place?: App.Place;
}

function ExperienceSearchOnlyAtLuxuryEscapesFilter({
  place,
  categories = EmptyArray,
}: Props & MappedProps) {
  const intl = useIntl()
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()
  const {
    categories: categoriesFacets,
    leExclusiveCount,
    luxPlusMemberPriceCount,
  } = useMostPopulatedSearchExperienceFacets(place)

  const luxPlusItems = useMemo((): Array<{
    titleLabel: React.ReactNode;
    count: number;
    value: number;
    checked: boolean;
  }> => {
    const childCategoriesByParent = groupBy(categories, (cat) => cat.parentId)
    const facetsById = arrayToObject(
      categoriesFacets,
      (facet) => facet.categoryId,
    )
    const luxPlusParent = categories.find(
      (cat) => cat.level === 'parent' && cat.id === EXP_CATEGORY_LUX_PLUS_ID,
    )

    if (!luxPlusParent) return []

    return (childCategoriesByParent.get(luxPlusParent.id) ?? []).map((cat) => {
      const count =
        cat.id === EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID
          ? luxPlusMemberPriceCount
          : facetsById[cat.id]?.count ?? 0
      const checked = !!searchFilters.luxPlusFeatures?.length
      let titleLabel: React.ReactNode = cat.name
      if (cat.id === EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID) {
        titleLabel = (
          <LuxPlusMemberPriceLabel>
            <LuxPlusLogo height={10} />
            <FormattedMessage
              id="tbedd0QfyCsU78aK5PLw"
              defaultMessage="Member price"
              description="Label text for LuxPlus+ member price checkbox option in experience search filters"
            />
          </LuxPlusMemberPriceLabel>
        )
      }
      return { titleLabel, count, value: cat.id, checked }
    })
  }, [categories, categoriesFacets, searchFilters.luxPlusFeatures, luxPlusMemberPriceCount])

  const onLuxPlusMemberPriceChange = useCallback(() => {
    setSearchFilters({
      ...searchFilters,
      luxPlusFeatures: searchFilters.luxPlusFeatures?.includes('Member prices')
        ? undefined
        : ['Member prices'],
    })
  }, [searchFilters, setSearchFilters])

  const onLeExclusiveChange = useCallback(() => {
    setSearchFilters({
      ...searchFilters,
      leExclusive: !searchFilters.leExclusive,
    })
  }, [searchFilters, setSearchFilters])

  return (
    <ListContainer>
      {luxPlusItems.map((item) => (
        <CheckboxInputListItem
          key={item.value}
          inputPlacement="start"
          checked={item.checked}
          onChange={onLuxPlusMemberPriceChange}
          title={item.titleLabel}
          size="medium"
          disabled={item.count === 0}
        >
          <BodyText variant="medium" colour="neutral-two">
            {intl.formatMessage(
              {
                id: 'aBh6DX-Fpw3aHf--5eSm',
                defaultMessage: '({count})',
                description:
                  'Count of matching experiences shown next to LuxPlus filter option',
              },
              { count: item.count },
            )}
          </BodyText>
        </CheckboxInputListItem>
      ))}
      <CheckboxInputListItem
        inputPlacement="start"
        checked={!!searchFilters.leExclusive}
        onChange={onLeExclusiveChange}
        title={intl.formatMessage({
          id: 'TxlMMKgor5yZq-d8fA7x',
          defaultMessage: 'Lux Exclusive',
          description: 'Tour category name for Luxury Escapes exclusive tours',
        })}
        size="medium"
        disabled={leExclusiveCount === 0}
      >
        <BodyText variant="medium" colour="neutral-two">
          {intl.formatMessage(
            {
              id: 'ZUgMu8eOGctwvCOmFaD7',
              defaultMessage: '({count})',
              description: 'Count of items for the Lux Exclusive filter option',
            },
            { count: leExclusiveCount },
          )}
        </BodyText>
      </CheckboxInputListItem>
    </ListContainer>
  )
}

function mapStateToProps(state: App.State) {
  return {
    categories: state.experience.experienceCategories,
  }
}

export default connect(mapStateToProps)(
  ExperienceSearchOnlyAtLuxuryEscapesFilter,
)
